import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PasswordFields,
  usePasswordValidation,
  FormField,
  Dialog,
  DialogBoddy,
  DialogFooter,
  DialogHeader,
} from '@/components';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (currentPassword: string, newPassword: string) => Promise<void>;
  isLoading: boolean;
}

export function ChangePasswordModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}: ChangePasswordModalProps) {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordValidation = usePasswordValidation(password, confirmPassword);

  const resetForm = useCallback(() => {
    setCurrentPassword('');
    setPassword('');
    setConfirmPassword('');
    setConfirmPasswordTouched(false);
    setError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    setError(null);

    if (!currentPassword) {
      setError(t('userProfile.errors.currentPasswordRequired'));
      return;
    }

    if (!passwordValidation.allRequirementsMet || !passwordValidation.passwordsMatch) {
      setError(t('userProfile.errors.invalidPassword'));
      return;
    }

    try {
      await onSubmit(currentPassword, password);
      resetForm();
      onClose();
    } catch {
      setError(t('userProfile.errors.changePasswordFailed'));
    }
  }, [currentPassword, password, passwordValidation, onSubmit, onClose, resetForm, t]);

  const handleCancel = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  return (
    <Dialog isOpen={isOpen} onClose={handleCancel}>
      <DialogHeader>{t('userProfile.changePassword')}</DialogHeader>
      <DialogBoddy className="text-onsurface pt-2">
        <div className="space-y-4">
          {error && (
            <div className="text-sm text-error bg-error/10 px-3 py-2 rounded-sm">
              {error}
            </div>
          )}
          <div>
            <FormField
              label={t('userProfile.fields.currentPassword')}
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder={t('userProfile.placeholders.currentPassword')}
            />
          </div>
          <PasswordFields
            password={password}
            confirmPassword={confirmPassword}
            onPasswordChange={setPassword}
            onConfirmPasswordChange={setConfirmPassword}
            confirmPasswordTouched={confirmPasswordTouched}
            onConfirmPasswordBlur={() => setConfirmPasswordTouched(true)}
          />
        </div>
      </DialogBoddy>


      <DialogFooter
        abortText={t('common.cancel')}
        submitText={isLoading ? t('common.saving') : t('userProfile.changePasswordButton')}
        onSubmit={handleSubmit}
        abortButtonProps={{ disabled: isLoading }}
        submitButtonProps={{ disabled: isLoading }}
      />
    </Dialog>
  );
}
